import { createRoot } from 'react-dom/client';
import { WidgetId } from 'types/idTypes';

import WordCloud, { WordCloudProps } from './WordCloud';
import { CloudWord } from './WordCloud.types';

// allow 2 seconds max for the word cloud calculation
const MAX_CALCULATION_TIME = 2000;

/*
 * Calculate the positions and sizes of the words in the word cloud
 * @param wordCloudProps - The props to pass to the WordCloud component
 * @param widgetId - The id of the word cloud widget
 *
 * @returns A promise that resolves to an array of CloudWord objects
 */
export const calculateWordCloudPostions = async (
  wordCloudProps: WordCloudProps,
  widgetId: WidgetId,
): Promise<CloudWord[]> => {
  return new Promise((resolve, reject) => {
    const container = document.createElement('div');
    let timeoutId: NodeJS.Timeout;

    const cleanup = () => {
      clearTimeout(timeoutId);
      root.unmount();
      document.body.removeChild(container);
    };

    // when the word cloud calculation is complete, resolve the promise and cleanup
    const onComplete = (words: CloudWord[]) => {
      if (words && words.length) {
        // wait one render cycle before cleaning up
        setTimeout(() => {
          cleanup();
          resolve(words);
        }, 0);
      }
    };

    document.body.appendChild(container);
    const root = createRoot(container);

    root.render(<WordCloud {...wordCloudProps} onComplete={onComplete} />);

    // Set a 3-second max time for the word cloud calculation
    timeoutId = setTimeout(() => {
      cleanup();
      reject(new Error(`WordCloud calculation timed out for: ${widgetId}`));
    }, MAX_CALCULATION_TIME);
  });
};
